import { useEffect, useState } from 'react';
import { EarconName, useEarcon } from '../../../../hooks/useEarcon';
import { useIsRTL } from '../../../../locale';
import { createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { getOSName, OSName } from '../../../../util';
import { useDirectSpeech } from '../../../DirectSpeech';
import bundle from '../../NovaComponents-TileCarousel-gui-icu.properties';
const useTextLocalizer = createUseTextLocalizer(bundle);
export const useTileCarouselKeyboardNav = ({
  tileRefs,
  tilesPerPage,
  numberOfTiles,
  scrollableContentRef,
  initialFocusableIndex,
  onSelect,
  reorderTiles,
  tiles
}) => {
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const directSpeech = useDirectSpeech();
  const [focusableIndex, setFocusableIndex] = useState(initialFocusableIndex ?? 0);
  const isMacOS = getOSName() === OSName.Mac;
  useEffect(() => {
    const focusableTile = tileRefs[focusableIndex];
    // Focus on the tile if it does not already have focus and one of the tiles has the focus
    if (!focusableTile?.contains(document.activeElement) && scrollableContentRef.current?.contains(document.activeElement)) {
      focusableTile?.focus();
    }

    // Update the tabindex for tiles via props.children so that only the currently focusable tile has tabindex of 0
    for (const [index, tile] of tileRefs.entries()) {
      tile?.setAttribute('tabindex', index === focusableIndex ? '0' : '-1');
    }
  }, [focusableIndex, scrollableContentRef, tileRefs]);
  const onReorder = reorderTiles ? (oldIndex, newIndex) => {
    reorderTiles?.(oldIndex, newIndex);
    const tile = tiles?.[newIndex];
    const ariaLabelledByText = tile?.ariaLabelledBy ? document.querySelector(`#${tile?.ariaLabelledBy}`)?.textContent : undefined;
    directSpeech?.notify(localize(newIndex < oldIndex ? 'moveTileBefore.aria' : 'moveTileAfter.aria', {
      tileName: ariaLabelledByText ?? tile?.ariaLabel ?? tile?.title ?? '',
      position: (newIndex + 1).toString(),
      numberOfTiles: numberOfTiles.toString()
    }));
  } : undefined;
  const handleKeyDown = e => {
    const isCtrlOrCmdPressed = isMacOS ? e.metaKey : e.ctrlKey;
    switch (e.key) {
      case 'ArrowDown': /* falls-through */
      case 'ArrowUp': /* falls-through */
      case 'ArrowLeft': /* falls-through */
      case 'ArrowRight':
        {
          const offset = e.key === 'ArrowDown' || isRTL && e.key === 'ArrowLeft' || !isRTL && e.key === 'ArrowRight' ? 1 : -1;
          e.persist();
          const nextIndex = Math.max(0, Math.min(numberOfTiles - 1, focusableIndex + offset));
          // Sounds earcon when going beyond the beginning or end of tile carousel
          if (focusableIndex === nextIndex) earcon.play();else {
            if (e.shiftKey && isCtrlOrCmdPressed && onReorder) {
              onReorder(focusableIndex, nextIndex);
            } else if (e.shiftKey) {
              onSelect(nextIndex, {
                selectionArea: 'tile',
                ctlKey: e.ctrlKey,
                metaKey: e.metaKey,
                shiftKey: e.shiftKey,
                altKey: e.altKey
              });
            }
            setFocusableIndex(nextIndex);
          }
          break;
        }
      case 'Home':
        {
          if (focusableIndex === 0) earcon.play();else {
            if (e.shiftKey && isCtrlOrCmdPressed && onReorder) {
              onReorder(focusableIndex, 0);
            }
            setFocusableIndex(0);
          }
          break;
        }
      case 'End':
        {
          if (focusableIndex === numberOfTiles - 1) earcon.play();else {
            if (e.shiftKey && isCtrlOrCmdPressed && onReorder) {
              onReorder(focusableIndex, numberOfTiles - 1);
            }
            setFocusableIndex(numberOfTiles - 1);
          }
          break;
        }
      case 'PageDown':
        {
          const nextIndex = Math.min(numberOfTiles - 1, focusableIndex + tilesPerPage);
          if (focusableIndex === nextIndex) earcon.play();
          setFocusableIndex(nextIndex);
          break;
        }
      case 'PageUp':
        {
          const nextIndex = Math.max(0, focusableIndex - tilesPerPage);
          if (focusableIndex === nextIndex) earcon.play();
          setFocusableIndex(nextIndex);
          break;
        }
      default:
        return;
    }
    e.preventDefault();
  };
  return {
    handleKeyDown,
    focusableIndex,
    setFocusableIndex
  };
};
//# sourceMappingURL=useTileCarouselKeyboardNav.js.map